import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import chatService, { streamFailureKey } from '../../../services/chatService';
import { CADMarker } from '../../../types/cad';
import { markersEqual, parseMarkers } from './markers';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Source text `content` holds as placeholders; expand only into text nodes. */
  literalValues?: string[];
}

interface UseCADChatOptions {
  modelId: string | null;
  modelName?: string;
  onMarkersUpdate?: (markers: CADMarker[]) => void;
  comparisonModelId?: string;
  comparisonModelName?: string;
  /** Reports whether there is a transcript or an unsent draft to lose. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Ephemeral CAD chat: creates a thread on first message, streams via SSE, and
 * resets whenever the model or the comparison target changes.
 */
export function useCADChat({
  modelId,
  modelName,
  onMarkersUpdate,
  comparisonModelId,
  comparisonModelName,
  onDirtyChange,
}: UseCADChatOptions) {
  const { t } = useTranslation();
  const isCompareMode = Boolean(comparisonModelId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamIdRef = useRef(0);
  const lastMarkersRef = useRef<CADMarker[]>([]);
  const onMarkersUpdateRef = useRef(onMarkersUpdate);
  onMarkersUpdateRef.current = onMarkersUpdate;
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  // Reset on model change or comparison mode change
  useEffect(() => {
    streamIdRef.current += 1;
    setMessages([]);
    setInput('');
    setThreadId(null);
    setLoading(false);
    lastMarkersRef.current = [];
    onMarkersUpdateRef.current?.([]);
  }, [modelId, comparisonModelId]);

  useEffect(() => {
    onDirtyChangeRef.current?.(messages.length > 0 || input.trim().length > 0);
  }, [messages, input]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || !modelId || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    const currentStreamId = ++streamIdRef.current;

    try {
      // Create thread lazily on first message
      let currentThreadId = threadId;
      if (!currentThreadId) {
        const title = isCompareMode
          ? t('cad.chat.threadCompareTitle', {
              modelA: modelName || '?',
              modelB: comparisonModelName || '?',
            })
          : t('cad.chat.threadTitle', { model: modelName || modelId.slice(0, 8) });
        const thread = await chatService.createThread(title);
        currentThreadId = thread.id;
        setThreadId(currentThreadId);
      }

      // Add empty assistant message for streaming
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      await chatService.streamMessage(
        currentThreadId,
        userMessage,
        (content) => {
          if (streamIdRef.current !== currentStreamId) return;
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content };
            return updated;
          });
          // Parse and emit markers (only if changed)
          const newMarkers = parseMarkers(content);
          if (!markersEqual(newMarkers, lastMarkersRef.current)) {
            lastMarkersRef.current = newMarkers;
            onMarkersUpdateRef.current?.(newMarkers);
          }
        },
        undefined, // onSearchResults - not used for CAD mode
        undefined, // onComplete
        {
          ...(isCompareMode
            ? { mode: 'cad_compare', modeData: { model_id_a: modelId, model_id_b: comparisonModelId } }
            : { mode: 'cad', modeData: { model_id: modelId } }),
          onLiteralValues: (literalValues) => {
            if (streamIdRef.current !== currentStreamId) return;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { ...updated[updated.length - 1], literalValues };
              return updated;
            });
          },
        }
      );
    } catch (err: any) {
      if (streamIdRef.current === currentStreamId) {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant') {
            // The answer is persisted after it streams, so a failure can arrive
            // with text already on screen. Annotating rather than only handling
            // the empty case: a kept-but-unsaved answer looked successful and
            // then vanished on reload (#1086).
            const notice = t(streamFailureKey(err) ?? 'cad.chat.error');
            updated[updated.length - 1] = {
              ...last,
              content: last.content ? `${last.content}\n\n${notice}` : notice,
            };
          }
          return updated;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [input, modelId, modelName, loading, threadId, isCompareMode, comparisonModelId, comparisonModelName, t]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return { messages, input, setInput, loading, handleSend, handleKeyDown, messagesEndRef };
}
